import { Navigate, Outlet } from 'react-router-dom';
import { useMyCompetitions } from '@/hooks/useCompetitions';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useAuthStore } from '@/store/authStore';
import { isSuperAdmin } from '@/lib/roles';
import { PageSpinner } from '@/components/ui/Spinner';
import type { CompetitionRoleRequirement } from '@/routes/CompetitionRoleRoute';
import { ParticipantStatus } from '@/api/types';

/**
 * Guards routes with no :competitionId in the URL (e.g. "/organizer/competitions",
 * "/judge/scoring", "/profile") - these are portal landing pages, not scoped
 * to one specific competition, so access is "does the caller qualify for
 * this role on AT LEAST ONE competition" via GET /users/me/competitions,
 * rather than a per-competition check (see CompetitionRoleRoute for that).
 *
 * "manage" additionally admits SuperAdmins and members of any organization:
 * the page behind it is where the first competition gets created, so
 * requiring an existing managed competition would be a dead end.
 */
export function AnyCompetitionRoleRoute({ allow }: { allow: CompetitionRoleRequirement[] }) {
  const user = useAuthStore((s) => s.user);
  const { data, isLoading } = useMyCompetitions();
  const orgs = useOrganizations({ page: 1, pageSize: 1 });

  if (isLoading || (allow.includes('manage') && orgs.isLoading)) return <PageSpinner />;

  const competitions = data?.items ?? [];
  const permitted = competitions.some(
    (c) =>
      (allow.includes('manage') && c.canManage) ||
      (allow.includes('judge') && c.isJudge) ||
      (allow.includes('participant') && c.participantStatus === ParticipantStatus.Approved),
  );

  const canManageOrg =
    allow.includes('manage') && (isSuperAdmin(user) || (orgs.data?.items.length ?? 0) > 0);

  if (!permitted && !canManageOrg) return <Navigate to="/" replace />;

  return <Outlet />;
}
